import { defineStore } from 'pinia';

interface BaseLocation {
  description: string;
  id: number;
  localization_key: string;
  name: string;
}

export interface LocationNode extends BaseLocation {
  locations?: LocationNode[] | null;
  type: string;
}

interface AugmentedLocation extends LocationNode {
  parent: AugmentedLocation | null;
  selected: boolean;
  token: string;
  value: string;
}

export interface SiteSearchResult {
  key: string;
  site: LocationNode;
  ancestors: LocationNode[];
  path: LocationNode[];
}

type LocationLabel = (location: LocationNode) => unknown;

interface Referral {
  id: number;
  name: string;
  referral_sites: BaseLocation[];
}

interface BaseResponse {
  name: string;
  selected: boolean;
  value: string;
  token: string;
}

interface EncounterResponse extends BaseResponse {
  id: number;
  description: string;
  location_type: string;
  panel: string;
  panel_input: 'multiselect';
  panel_type: 'select';
}

interface ReferralResponse extends BaseResponse {
  id: string;
  ordering: number;
  unknown: boolean;
}

interface LocationSummary {
  collections: string;
  editRoutePath: string;
  id: number;
  labelToken: string;
  name: string;
  responses: EncounterResponse[];
  type: string;
}

interface LocationState {
  current: AugmentedLocation | null;
  locations: AugmentedLocation[];
  rootLocations: LocationNode[];
  referrals: Referral[];
  selectionMade: boolean;
}

// from conf.json
interface EngagementSettingsConfig {
  locations: LocationNode[] | null;
  referrals: Referral[] | null;
}

// helper functions

export const locationToken = (location: LocationNode): string =>
  location.localization_key ||
  `responses.encounter_${location.type}.${location.description}.title`;

export const filterSites = (
  sites: SiteSearchResult[],
  query: string,
  labelFor: LocationLabel,
  locale?: string,
): SiteSearchResult[] => {
  const normalized = query.trim().toLocaleLowerCase();
  const matches = !normalized ? sites.slice() : sites.filter(({ site }) => [
    labelFor(site),
    site.name,
    site.description,
  ].filter(Boolean).some(value =>
    String(value).toLocaleLowerCase().includes(normalized)
  ));
  // Sort displayed labels without reordering the stored location hierarchy.
  return matches.sort((left, right) => String(labelFor(left.site)).localeCompare(
    String(labelFor(right.site)), locale, { sensitivity: 'base' },
  ));
};

export const siteIsSelected = (
  current: LocationNode | null,
  result: SiteSearchResult,
): boolean => Boolean(current) &&
  String(current?.id ?? current?.name) === String(result.site.id ?? result.site.name);

const augmentLocations = (
  locations: LocationNode[] | AugmentedLocation[] | null | undefined,
  currentLocation: AugmentedLocation | null = null
): AugmentedLocation[] => {
  if (!locations) {
    return [];
  }

  return locations.map((location) => {
    const value = (location as AugmentedLocation).value || location.name;
    const locToken = locationToken(location);

    let selected = false;
    let parent: AugmentedLocation | null = null;

    if (currentLocation) {
      const { locations, ...rest } = currentLocation;
      parent = { ...rest, locations: null };
      selected = currentLocation.value === value;
    }

    return {
      ...location,
      parent,
      selected,
      token: locToken,
      value,
    };
  });
};

const flattenSites = (
  locations: LocationNode[] = [],
  ancestors: LocationNode[] = [],
): SiteSearchResult[] => locations.flatMap((location) => {
  const path = [...ancestors, location];
  const current = location.type === 'site'
    ? [{
      key: `${location.type}:${location.id ?? location.name}`,
      site: location,
      ancestors,
      path,
    }]
    : [];

  return [
    ...current,
    ...flattenSites(location.locations || [], path),
  ];
});

const augmentSelectedLocation = (
  location: LocationNode,
  parent: AugmentedLocation | null = null,
): AugmentedLocation => {
  const rest = { ...location };
  delete rest.locations;

  return {
    ...rest,
    selected: true,
    token: locationToken(location),
    value: location.name,
    parent,
  };
};

const buildSelectedLocation = (path: LocationNode[]): AugmentedLocation | null =>
  path.reduce<AugmentedLocation | null>(
    (parent, location) => augmentSelectedLocation(location, parent),
    null,
  );

const buildResponses = (
  location: AugmentedLocation | null | undefined,
  responses: Record<string, EncounterResponse> = {}
): Record<string, EncounterResponse> => {
  if (!location) {
    return responses;
  }

  const { id, name, description, selected, type, token, value } = location;
  const key = `encounter_${type}`;

  responses[key] = {
    id,
    name,
    description,
    selected,
    token,
    value,
    location_type: type,
    panel: key,
    panel_input: 'multiselect',
    panel_type: 'select',
  };

  return buildResponses(location.parent, responses);
};

const baseLocationToResponse = (loc: BaseLocation): ReferralResponse => {
  return {
    id: loc.name,
    name: `encounter_referral.${loc.name}`,
    ordering: 0,
    selected: false,
    unknown: false,
    value: loc.name,
    token: loc.localization_key,
  }
}

export const useLocationStore = defineStore('location', {
  persist: true,
  state: (): LocationState => ({
    current: null,
    locations: [],
    referrals: [],
    rootLocations: [],
    selectionMade: false,
  }),

  getters: {
    sites(state): SiteSearchResult[] {
      return flattenSites(state.rootLocations);
    },

    locationsIncomplete(state): boolean {
      return state.rootLocations.length > 0 && !this.selectionsComplete
    },

    responses(state): Record<string, EncounterResponse> {
      return buildResponses(state.current);
    },

    referralResponses(state): ReferralResponse[] {
      const currentLocationName = state.current?.name;
      const group = state.referrals.find(loc => loc.name === currentLocationName);
      if (!group) return []
      return group.referral_sites.map(baseLocationToResponse)
    },

    selectionsComplete(state): boolean {
      return !!state.current && !state.current?.locations;
    },

    siteSummary(state): LocationSummary | null {
      if (!state.current) {
        return null;
      }
      const site = (this.responses as Record<string, EncounterResponse>)[
        `encounter_${state.current.type}`
      ];
      if (!site) {
        return null;
      }
      return {
        collections: 'location',
        editRoutePath: '/location',
        id: 0,
        labelToken: 'app.summary.site.title',
        name: 'site',
        responses: [site],
        type: 'location',
      };
    },

    siteLocationSummary(state): LocationSummary | null {
      if (!state.current) {
        return null;
      }
      const ancestors = (Object.values(this.responses) as EncounterResponse[])
        .filter((response) => response.location_type !== state.current?.type)
        .reverse();
      if (!ancestors.length) {
        return null;
      }
      return {
        collections: 'location',
        editRoutePath: '/location',
        id: 1,
        labelToken: 'app.summary.site_location.title',
        name: 'site_location',
        responses: ancestors,
        type: 'location',
      };
    },
  },

  actions: {
    selectSite(result: SiteSearchResult): void {
      this.current = buildSelectedLocation(result.path);
      this.locations = [];
      this.selectionMade = true;
    },

    initialize(): void {
      if (this.current) {
        this.current = null;
        this.locations = [];
        this.selectionMade = false;
      }
      this.locations = augmentLocations(this.rootLocations);
    },

    next(nextPanelAction: () => void): void {
      if (this.selectionsComplete) {
        nextPanelAction();
      } else {
        this.update();
      }
    },

    set(location: string | null): void {
      if (location && location !== 'unknown') {
        const locationObj = this.locations.find((loc) => loc.value === location);
        if (locationObj) {
          this.current = locationObj;
          this.selectionMade = true;
        }
      }
    },

    update(): void {
      if (this.current) {
        this.locations = augmentLocations(this.current.locations, this.current);
        this.selectionMade = false;
      }
    },

    loadFromConfig(config: EngagementSettingsConfig) {
      this.referrals = config.referrals || [];
      this.rootLocations = config.locations || [];
    },
  },
})
